import { Heart, MessageCircle, Share2, MoreHorizontal, Plus, Search } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';

const posts = [
  {
    id: 1,
    user: { name: 'Elena Gilbert', avatar: 'https://picsum.photos/seed/elena/100/100', handle: '@elena_g' },
    content: 'Just finished the new branding for Messego. What do you guys think? 🎨✨',
    image: 'https://picsum.photos/seed/design/800/600',
    likes: '1.2k',
    comments: '48',
    time: { key: 'common.hoursAgo', n: 2 }
  },
  {
    id: 2,
    user: { name: 'Marcus Aurelius', avatar: 'https://picsum.photos/seed/marcus/100/100', handle: '@philosopher' },
    content: 'The secret of all victory lies in the organization of the non-obvious.',
    likes: '856',
    comments: '12',
    time: { key: 'common.hoursAgo', n: 5 }
  },
  {
    id: 3,
    user: { name: 'Tech Insider', avatar: 'https://picsum.photos/seed/tech/100/100', handle: '@tech_insider' },
    content: 'Messego is revolutionizing how we handle unified communications and payments. A true game changer for freelancers. 🚀',
    image: 'https://picsum.photos/seed/rocket/800/600',
    likes: '3.4k',
    comments: '156',
    time: { key: 'common.daysAgo', n: 1 }
  }
];

export default function Social() {
  const { t } = useTranslation();
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <header className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">{t('social.title')}</h1>
        <div className="flex items-center gap-2">
          <button className="p-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
            <Search className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-bold hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
            <Plus className="w-4 h-4" />
            {t('social.createPost')}
          </button>
        </div>
      </header>

      {/* Stories / Active Users */}
      <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
        <div className="flex flex-col items-center gap-2 flex-shrink-0">
          <div className="w-16 h-16 rounded-full border-2 border-dashed border-neutral-300 dark:border-neutral-700 flex items-center justify-center bg-white dark:bg-neutral-900 cursor-pointer hover:border-neutral-900 transition-colors">
            <Plus className="w-6 h-6 text-neutral-400 dark:text-neutral-500" />
          </div>
          <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400">{t('social.yourStory')}</span>
        </div>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex flex-col items-center gap-2 flex-shrink-0">
            <div className="w-16 h-16 rounded-full p-0.5 border-2 border-neutral-900 cursor-pointer">
              <div className="w-full h-full rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-800 border-2 border-white">
                <img src={`https://picsum.photos/seed/story${i}/100/100`} alt={t('social.storyAlt')} referrerPolicy="no-referrer" />
              </div>
            </div>
            <span className="text-[10px] font-bold text-neutral-900 dark:text-neutral-50">{t('social.user', { n: i })}</span>
          </div>
        ))}
      </div>

      {/* Feed */}
      <div className="space-y-6">
        {posts.map((post) => (
          <article key={post.id} className="bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-800">
                  <img src={post.user.avatar} alt={post.user.name} referrerPolicy="no-referrer" />
                </div>
                <div>
                  <p className="font-bold text-sm">{post.user.name}</p>
                  <p className="text-[10px] text-neutral-500 dark:text-neutral-400 font-medium">{post.user.handle} • {t(post.time.key, { n: post.time.n })}</p>
                </div>
              </div>
              <button className="p-2 rounded-full hover:bg-neutral-50 dark:hover:bg-neutral-950">
                <MoreHorizontal className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              </button>
            </div>
            
            <div className="px-4 pb-4">
              <p className="text-sm leading-relaxed text-neutral-800">{post.content}</p>
            </div>

            {post.image && (
              <div className="aspect-video w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                <img src={post.image} alt={t('social.postImageAlt')} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              </div>
            )}

            <div className="p-4 flex items-center justify-between border-t border-neutral-50">
              <div className="flex items-center gap-6">
                <button className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 hover:text-rose-500 dark:hover:text-rose-400 transition-colors group">
                  <Heart className="w-5 h-5 group-hover:fill-rose-500" />
                  <span className="text-xs font-bold">{post.likes}</span>
                </button>
                <button className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-xs font-bold">{post.comments}</span>
                </button>
                <button className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              <button className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest hover:text-neutral-900 dark:hover:text-neutral-50">{t('social.save')}</button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
